import { useState, useRef, useCallback } from "react";

/**
 * Custom hook for mobile-specific interactions using Pointer Events API
 * Handles touch, mouse, and stylus interactions with drag detection for carousels
 */
export const useMobileInteractions = (isMobile: boolean) => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const dragThreshold = 10;
  const carouselRef = useRef<HTMLUListElement>(null);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (!isMobile || !carouselRef.current) return;

      // Don't interfere with interactive elements (buttons, links, etc.)
      const target = e.target as HTMLElement;
      const isInteractive = target.closest(
        'button, a, [role="button"], [tabindex="0"]',
      );

      if (isInteractive) {
        return;
      }

      // Capture pointer to receive subsequent events even if pointer moves outside element
      e.currentTarget.setPointerCapture(e.pointerId);
      setStartX(e.pageX - carouselRef.current.offsetLeft);
    },
    [isMobile],
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isMobile || !carouselRef.current) return;

      // Only handle if we have pointer capture
      if (!e.currentTarget.hasPointerCapture(e.pointerId)) return;

      const x = e.pageX - carouselRef.current.offsetLeft;
      const distance = Math.abs(x - startX);

      // Set dragging state if movement exceeds threshold
      if (distance > dragThreshold) {
        setIsDragging(true);
      }
    },
    [isMobile, startX, dragThreshold],
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent) => {
      if (!isMobile) return;

      // Release pointer capture
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      setIsDragging(false);
    },
    [isMobile],
  );

  return {
    pointerHandlers: {
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: handlePointerUp,
      onPointerCancel: handlePointerUp,
    },
    carouselRef,
    isDragging,
  };
};
